import axios from 'axios';

// Backend base URL. Driven by REACT_APP_PROJECT_URL so the backend can be moved
// (or pointed at a local rebuild) without a code change — set it in Vercel's
// environment variables, or in .env.local for local development.
//
// Note: Create React App inlines REACT_APP_* at BUILD time, not runtime, so
// changing it in Vercel requires a redeploy to take effect.
//
// The fallback is production, so a missing variable degrades to the right host
// rather than to localhost.
export const PROJECT_URL =
    process.env.REACT_APP_PROJECT_URL || 'https://mvl.shrivastech.com/';
export const API_URL = `${PROJECT_URL}wp-json/wc/v3`;
// WooCommerce Store API: public, read-only, no authentication required.
// Purpose-built for headless storefronts and exposes only customer-safe fields,
// unlike wc/v3 which needs elevated capabilities and returns internal metadata.
export const STORE_API_URL = `${PROJECT_URL}wp-json/wc/store/v1`;
export const WP_MEDIA_API_URL = `${PROJECT_URL}wp-json/wp/v2/media`;
export const WP_TERMS_API_URL = `${PROJECT_URL}wp-json/wp/v2/media_folder`;
export const WP_USER_API_URL = `${PROJECT_URL}wp-json/wc/v3/customers`;
export const SHIPMENT_TRACKING_API_URL = `${PROJECT_URL}wp-json/wc-shipment-tracking/v3`;
export const JWT_AUTH_URL = `${PROJECT_URL}wp-json/jwt-auth/v1/token`;
export const LOCATION_URL = `${PROJECT_URL}wp-json/custom/v1/pincodes`;
export const COUPON_URL = `${PROJECT_URL}wp-json/wc/v3/coupons`;
export const GST_API_URL = `${PROJECT_URL}wp-json/custom/v1/calculate-gst`;

const REQUEST_TIMEOUT = 60000; // 60 seconds
let isRefreshingToken = false;
let tokenPromise = null;

// Credentials for the anonymous-browsing fallback account, used only when no real
// user token is present; signed-in users get their own JWT (see getAuthToken).
//
// No hardcoded default. Both values come from configuration, so no account name or
// password is embedded in the source. Set them in Vercel (and .env.development.local
// for local work).
//
// The account is scoped to the `headless_reader` role — public catalog reads only,
// with orders, customers and coupons all returning 403. CRA inlines REACT_APP_* into
// the bundle at build time, so this is not secret storage; it is tolerable only
// because the account is deliberately low-privilege.
//
// The real fix is to remove this account entirely and read the catalog from the
// unauthenticated Store API (wc/store/v1) — see docs/MIGRATION_MANIFEST.md.
const STOREFRONT_USER = process.env.REACT_APP_STOREFRONT_USER;
const STOREFRONT_PASS = process.env.REACT_APP_STOREFRONT_PASS;

// Login function to fetch Bearer token
const loginAndGetToken = async () => {
    // Fail loudly and specifically rather than POSTing `undefined`, which returns a
    // misleading "incorrect_password" and sends you hunting the wrong problem.
    if (!STOREFRONT_USER || !STOREFRONT_PASS) {
        throw new Error(
            'Storefront credentials are not configured. Set REACT_APP_STOREFRONT_USER and ' +
            'REACT_APP_STOREFRONT_PASS in the environment and rebuild (CRA inlines these at build time).'
        );
    }

    try {
        const response = await axios.post(JWT_AUTH_URL, {
            username: STOREFRONT_USER,
            password: STOREFRONT_PASS,
        });
        const token = response.data.token;
        localStorage.setItem('auth_token', token);
        return token;
    } catch (error) {
        console.error('Login failed:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
        });
        throw new Error('Failed to authenticate user');
    }
};

// Is there a real signed-in user, as opposed to anonymous browsing?
//
// This matters because the anonymous fallback must NEVER be used to replace a real
// user's session. Doing so silently swaps the caller's identity to the shared
// storefront account — which is how a signed-in customer ended up seeing
// free_user's profile, and would have meant orders attributed to the wrong account.
export const hasUserSession = () => {
    try {
        return Boolean(localStorage.getItem('user_data'));
    } catch {
        return false;
    }
};

// Clear a real user's session. Used when their token is rejected: the correct
// outcome is "you are signed out", never "you are now the shared account".
const clearUserSession = () => {
    try {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        localStorage.setItem('isAuthenticated', 'false');
    } catch {
        /* storage unavailable — nothing useful to do */
    }
};

// Get or refresh token
const getAuthToken = async () => {
    let token = localStorage.getItem('auth_token');
    if (token) return token;

    // No token AND the app believes a user is signed in: their session is gone.
    // Falling back to the storefront account here would silently impersonate it.
    if (hasUserSession()) {
        clearUserSession();
        throw new Error('Your session has expired. Please sign in again.');
    }

    if (!isRefreshingToken) {
        isRefreshingToken = true;
        tokenPromise = loginAndGetToken().finally(() => {
            isRefreshingToken = false;
        });
    }
    return tokenPromise;
};

/* ===========================================================================
   CATALOG READS MUST NOT USE THE SIGNED-IN USER'S TOKEN

   The catalog is public data, but WooCommerce serves it from `wc/v3`, which is
   the *admin* API: `GET wc/v3/products` requires `read_private_products` and
   `GET wc/v3/products/categories` requires `manage_product_terms`. The
   `customer` role has neither — it has exactly one capability, `read`. Verified
   against the live backend with a real customer token: both return
   403 `woocommerce_rest_cannot_view`.

   So the moment a customer signed in and `auth_token` became *their* JWT, every
   catalog read started failing and the storefront showed no products.

   This was invisible before the SEC-21 fix because the old interceptor reacted
   to any 403 by re-authenticating as the shared storefront account and retrying
   — which succeeded. Catalog reads were only ever working *because* of the
   identity swap that SEC-21 removed. Fixing the swap exposed the dependency.

   The fix is to stop conflating the two tokens:

     auth_token        the signed-in person (or the storefront account when
                       browsing anonymously) — used for customer actions
     storefront_token  the `headless_reader` account, ALWAYS used for catalog
                       reads regardless of who is signed in

   Keeping them under separate keys is the point: a single key means logging in
   overwrites the catalog's credentials, which is precisely this bug.

   The real fix remains ARCH-5 — read the catalog from the unauthenticated Store
   API so no account is needed at all. That is blocked on RX-1/RX-2 because the
   Store API does not expose `purchase_note`, which gates prescriptions on 70%
   of the catalog. Until then, catalog reads need their own token.
   =========================================================================== */

const STOREFRONT_TOKEN_KEY = 'storefront_token';
let isRefreshingStorefrontToken = false;
let storefrontTokenPromise = null;

const fetchStorefrontToken = async () => {
    if (!STOREFRONT_USER || !STOREFRONT_PASS) {
        throw new Error(
            'Storefront credentials are not configured. Set REACT_APP_STOREFRONT_USER and ' +
            'REACT_APP_STOREFRONT_PASS in the environment and rebuild (CRA inlines these at build time).'
        );
    }
    const response = await axios.post(JWT_AUTH_URL, {
        username: STOREFRONT_USER,
        password: STOREFRONT_PASS,
    });
    const token = response.data.token;
    try {
        localStorage.setItem(STOREFRONT_TOKEN_KEY, token);
    } catch {
        /* storage unavailable — the in-memory promise still serves this session */
    }
    return token;
};

const getStorefrontToken = async () => {
    let token = null;
    try {
        token = localStorage.getItem(STOREFRONT_TOKEN_KEY);
    } catch {
        token = null;
    }
    if (token) return token;

    // De-duplicate concurrent refreshes — a cold page load fires several catalog
    // requests at once, and each would otherwise log in separately.
    if (!isRefreshingStorefrontToken) {
        isRefreshingStorefrontToken = true;
        storefrontTokenPromise = fetchStorefrontToken().finally(() => {
            isRefreshingStorefrontToken = false;
        });
    }
    return storefrontTokenPromise;
};

const clearStorefrontToken = () => {
    try {
        localStorage.removeItem(STOREFRONT_TOKEN_KEY);
    } catch {
        /* nothing useful to do */
    }
};

// Create Axios instance with Bearer token
const api = axios.create({
    baseURL: API_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add Bearer token
api.interceptors.request.use(async (config) => {
    const token = await getAuthToken();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Response interceptor for 401/403.
//
// This used to re-login as the anonymous storefront account on ANY 401/403 and retry,
// regardless of who was signed in. For a signed-in customer that silently replaced
// their identity with the shared account: subsequent requests ran as free_user, the
// profile page showed free_user's details, and an order would have been attributed to
// the wrong account. It went unnoticed for a long time because free_user used to be an
// Administrator, so the swapped session could still do everything.
//
// Correct behaviour depends on who is signed in:
//   real user  -> their token is invalid; sign them out and surface it. Never
//                 substitute a different account.
//   anonymous  -> the storefront token expired; re-fetching it is the right fix.
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error.response?.status;
        if (status !== 401 && status !== 403) {
            throw error;
        }

        // A 403 on a valid session usually means "not allowed", not "bad token" —
        // retrying cannot help, and re-authenticating would mask a real permission
        // problem. Only a 401 indicates the token itself is the issue.
        if (hasUserSession()) {
            if (status === 401) {
                clearUserSession();
                throw new Error('Your session has expired. Please sign in again.');
            }
            throw error;
        }

        // Anonymous browsing: refresh the storefront token once and retry.
        if (error.config?._retriedAfterAuth) {
            throw error;
        }
        try {
            localStorage.removeItem('auth_token');
            const token = await loginAndGetToken();
            error.config._retriedAfterAuth = true;
            error.config.headers.Authorization = `Bearer ${token}`;
            return await axios(error.config);
        } catch (retryError) {
            console.error('Retry failed after refreshing token:', retryError);
            throw retryError;
        }
    }
);

// Generic request method
export async function request(method, url, data = {}, params = {}, headers = {}) {
    try {
        const config = {
            method: method.toLowerCase(),
            url,
            data,
            params,
            headers: {
                ...headers,
                'Content-Type': headers['Content-Type'] || 'application/json',
            },
        };
        const response = await api(config);
        return response;
    } catch (error) {
        console.error(`${method.toUpperCase()} request failed:`, {
            url,
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            headers: error.response?.headers,
            config: error.config,
        });
        throw new Error(error.response?.data?.message || `${method.toUpperCase()} request failed: ${error.message}`);
    }
}

// Catalog instance: always the storefront account, never the signed-in user.
// See the block comment above for why these must be separate.
const catalogApi = axios.create({
    baseURL: API_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
    },
});

catalogApi.interceptors.request.use(async (config) => {
    const token = await getStorefrontToken();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// A 401 here means the storefront token expired — refresh it once and retry.
// A 403 means the storefront account genuinely lacks the capability, which is a
// configuration problem no retry can fix, so it is surfaced as-is.
catalogApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status !== 401 || error.config?._retriedAfterAuth) {
            throw error;
        }
        clearStorefrontToken();
        const token = await getStorefrontToken();
        error.config._retriedAfterAuth = true;
        error.config.headers.Authorization = `Bearer ${token}`;
        return axios(error.config);
    }
);

/**
 * Same signature as `request`, but authenticated as the storefront account.
 * Use for public catalog reads (products, categories); use `request` for
 * anything acting on behalf of the signed-in person.
 */
export async function catalogRequest(method, url, data = {}, params = {}, headers = {}) {
    try {
        return await catalogApi({
            method: method.toLowerCase(),
            url,
            data,
            params,
            headers: {
                ...headers,
                'Content-Type': headers['Content-Type'] || 'application/json',
            },
        });
    } catch (error) {
        console.error(`${method.toUpperCase()} catalog request failed:`, {
            url,
            message: error.message,
            status: error.response?.status,
            response: error.response?.data,
        });
        throw new Error(
            error.response?.data?.message || `${method.toUpperCase()} request failed: ${error.message}`
        );
    }
}

// Axios instance for media-related requests
const mediaApi = axios.create({
    baseURL: WP_MEDIA_API_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Axios instance for terms-related requests
const termsApi = axios.create({
    baseURL: WP_TERMS_API_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Apply interceptors to mediaApi and termsApi
[mediaApi, termsApi].forEach((instance) => {
    instance.interceptors.request.use(async (config) => {
        const token = await getAuthToken();
        config.headers.Authorization = `Bearer ${token}`;
        return config;
    });
    // Same reasoning as the main api interceptor above: never substitute the
    // anonymous storefront account for a real user's session.
    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const status = error.response?.status;
            if (status !== 401 && status !== 403) {
                throw error;
            }
            if (hasUserSession()) {
                if (status === 401) {
                    clearUserSession();
                    throw new Error('Your session has expired. Please sign in again.');
                }
                throw error;
            }
            if (error.config?._retriedAfterAuth) {
                throw error;
            }
            try {
                localStorage.removeItem('auth_token');
                const token = await loginAndGetToken();
                error.config._retriedAfterAuth = true;
                error.config.headers.Authorization = `Bearer ${token}`;
                return await axios(error.config);
            } catch (retryError) {
                throw retryError;
            }
        }
    );
});
export { api, mediaApi, termsApi, loginAndGetToken, getAuthToken };